<template>
  <div
    class="snippet"
    :class="{
      'snippet--image-variant': imageVariant,
      'snippet--fullwidth-variant': fullWidthVariant,
      'snippet--product-ads': carouselType === 'productAds',
    }"
  >
    <div class="snippet__header">
      <EHeading
        as="h2"
        class="snippet__title"
        data-test-id="snippet-section-title"
      >
        <slot name="heading">
          {{ title }}
        </slot>
      </EHeading>
      <slot name="header-end" />
    </div>
    <EBaseCarousel
      ref="snippet__carousel"
      :snap="snap"
      class="snippet__carousel"
      :class="`snippet__carousel--${carouselScrollState}`"
      @scroll-state="onScrollState"
    >
      <template #previous-control="{ getCurrentScrollPosition, visible }">
        <transition name="fade">
          <EButton
            v-show="visible"
            class="snippet__control snippet__control--previous"
            icon="chevron-left"
            variant="outline"
            tabindex="-1"
            aria-hidden="true"
            data-test-id="result-snippet-control-previous"
            @click="onPrevious(getCurrentScrollPosition)"
            @mouseenter="onHovered('prev')"
            @mouseleave="onLeave('prev')"
          />
        </transition>
      </template>
      <div ref="list">
        <slot />
      </div>
      <template #next-control="{ getCurrentScrollPosition, visible }">
        <transition name="fade">
          <EButton
            v-show="visible"
            class="snippet__control snippet__control--next"
            icon="chevron-right"
            variant="outline"
            tabindex="-1"
            aria-hidden="true"
            data-test-id="result-snippet-control-next"
            @click="onNext(getCurrentScrollPosition)"
            @mouseenter="onHovered('next')"
            @mouseleave="onLeave('next')"
          />
        </transition>
      </template>
    </EBaseCarousel>
    <div v-if="$slots.footer" class="snippet__footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<script>
import CarouselController, { PixelBasedScrolling } from '~~/common/js/client/carousel';
import EBaseCarousel from '~~/common/vue/components/base-carousel';
import EButton from '~~/common/vue/components/button';
import EHeading from '~~/common/vue/components/heading';

export default {
  name: 'EBaseSnippet',
  components: {
    EBaseCarousel,
    EButton,
    EHeading,
  },
  props: {
    imageVariant: {
      type: Boolean,
      default: false,
    },
    fullWidthVariant: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    // TODO: add isCarouselAlgorithm validator
    carouselAlgorithm: {
      type: Object,
      default: null,
    },
    carouselType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      snap: false,
      carouselClicked: false,
      carouselScrollState: 'start',
    };
  },
  computed: {
    scrollDistance() {
      return 20;
    },
    initScroll() {
      const carouselOptions = {
        container: this.$refs.list,
        algorithm: new PixelBasedScrolling(),
      };
      return new CarouselController(carouselOptions);
    },
  },
  mounted() {
    const carouselOptions = {
      container: this.$refs.list,
      items: Array.from(this.$refs.list.querySelectorAll('li')),
    };

    if (this.carouselAlgorithm) {
      carouselOptions.algorithm = this.carouselAlgorithm;
    }

    this.carousel = new CarouselController(carouselOptions);
  },
  methods: {
    onPrevious(getCurrentScrollPosition) {
      this.carousel.previous(getCurrentScrollPosition(), this.carouselType);
      this.carouselClicked = true;
    },
    onNext(getCurrentScrollPosition) {
      this.carousel.next(getCurrentScrollPosition(), this.carouselType);
      this.carouselClicked = true;
    },
    onHovered(direction) {
      if (this.carouselType === 'productAds') {
        this.initScroll.hovered(this.scrollDistance, direction);
      }
    },
    onLeave(direction) {
      if (this.carouselClicked === false) {
        if (this.carouselType === 'productAds') {
          this.initScroll.leave(this.scrollDistance, direction);
        }
      } else {
        this.carouselClicked = false;
      }
    },
    onScrollState(state) {
      this.carouselScrollState = state;
    },
  },
};
</script>

<style lang="scss" scoped>
$snippet-horizontal-spacing: 0;
$gradient-width: 72px;
// TODO: these should be using var(--background-color-primary) instead,
// which unfortunately is not possible for RGBA / gradient implementations,
// Also see search/images/src/components/query-expansions implementation
// for query expansion gradients - having the same issue.
$gradient-color: $white;
$gradient-color-dark: $c-gray-90;

.snippet {
  padding: $space-m;
}

.snippet__header {
  align-items: center;
  display: flex;
}

.snippet__title {
  color: var(--color-text-secondary);
  font-weight: $font-weight-m;
  margin: 0;
  font-size: $font-l;
  flex-grow: 1;
}

.snippet__carousel {
  margin: 0 -#{$space-m};
  position: relative;

  @include desktop {
    margin: 0 -#{$space-2s};
  }
}

.snippet__control {
  display: none;

  @include desktop {
    display: unset;
    position: absolute;
    top: 50%;
    z-index: $z-index-l;
  }
}

.snippet__control--previous {
  left: 0;
  transform: translate(-50%, -50%);
}

.snippet__control--next {
  right: $snippet-horizontal-spacing;
  transform: translate(50%, -50%);
}

// left and right gradients for product-ads carousel
.snippet--product-ads {
  @include desktop {
    .snippet__carousel--start::after,
    .snippet__carousel--end::before,
    .snippet__carousel:not(.snippet__carousel--start):not(.snippet__carousel--end)::after,
    .snippet__carousel:not(.snippet__carousel--start):not(.snippet__carousel--end)::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      width: $gradient-width;
      animation: e-fade-in $timing-s both;
    }

    .snippet__carousel--end::before,
    .snippet__carousel:not(.snippet__carousel--start):not(.snippet__carousel--end)::before {
      left: 0;
      background: linear-gradient(
        to left,
        scale-color($gradient-color, $alpha: -100%) 0%,
        $gradient-color 100%
      );
      z-index: $z-index-l;
    }

    .snippet__carousel--start::after,
    .snippet__carousel:not(.snippet__carousel--start):not(.snippet__carousel--end)::after {
      right: 0;
      background: linear-gradient(
        to right,
        scale-color($gradient-color, $alpha: -100%) 0%,
        $gradient-color 100%
      );
    }

    .snippet__carousel--start,
    .snippet__carousel--end,
    .snippet__carousel:not(.snippet__carousel--start):not(.snippet__carousel--end) {
      &:hover,
      &:focus {
        &::before,
        &::after {
          content: none;
        }
      }
    }
  }
}

// dark mode specific colours for product-ads carousel gradients
.dark .snippet--product-ads {
  @include desktop {
    .snippet__carousel--end::before,
    .snippet__carousel:not(.snippet__carousel--start):not(.snippet__carousel--end)::before {
      background: linear-gradient(
        to left,
        scale-color($gradient-color-dark, $alpha: -100%) 0%,
        $gradient-color-dark 100%
      );
    }

    .snippet__carousel--start::after,
    .snippet__carousel:not(.snippet__carousel--start):not(.snippet__carousel--end)::after {
      background: linear-gradient(
        to right,
        scale-color($gradient-color-dark, $alpha: -100%) 0%,
        $gradient-color-dark 100%
      );
    }
  }
}

.snippet--image-variant {
  .snippet__header {
    @include tablet {
      padding-right: $space-l;
    }
  }

  .snippet__footer {
    @include desktop {
      padding-left: $space-l;
      padding-right: $space-l;
    }
  }

  .snippet__control--previous {
    transform: translate(50%, -50%);
  }

  .snippet__control--next {
    transform: translate(-50%, -50%);
  }
}

.snippet--fullwidth-variant {
  .snippet__header {
    @include tablet {
      padding-right: $space-l;
    }
  }

  .snippet__control--previous {
    transform: translate(50%, -50%);
  }

  .snippet__control--next {
    transform: translate(-50%, -50%);
  }
}

.fade-enter-active,
.fade-leave-active,
.loading-button__content {
  transition: opacity $timing-1s $easing;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>

<style lang="scss">
// The unscoped `.snippet__item` class
// allows the first/last items in various snippets
// to have additional padding. This allows us to create the
// "scroll overflow" effect where items scroll out beyond the container's
// edge to the left and right. This has to be done individually for the items
// rather than for a containing div, because Chrome doesn't take the container's
// padding into account when calculating the snap position.
// Doubling up the class for higher specificity.
.snippet__item.snippet__item {
  // here we explicitly need pixels because we use this variable in calc()
  // stylelint-disable-next-line length-zero-no-unit
  --snippet-item-padding: 0px;

  @include desktop-down {
    &:first-of-type,
    &:last-of-type {
      --snippet-item-padding: #{$space-m};
    }

    &:first-of-type {
      padding-left: var(--snippet-item-padding);
    }

    &:last-of-type {
      padding-right: var(--snippet-item-padding);
    }
  }
}
</style>
